// BOL review service — the human-linking step for scans the pipeline
// could not resolve on its own, plus the portal-triggered shipping flow
// (sales-site bolScanProcess) that a verified link kicks off.

import api from './api';
import { downloadAuthedFile, openAuthedFile } from './authedFile';

export interface BolReviewItem {
  id: string;
  filename: string;
  status: string;
  sage_order_no: string | null;
  extraction_attempts: number;
  last_extraction_at: string | null;
  drive_web_view_link: string | null;
  drive_modified_time: string;
  /** Content-derived name ("Customer PO NA273743 - 2026-08-19 p5.pdf"); null on old rows. */
  display_name?: string | null;
  page_number?: number | null;
}

/** Bearer-authenticated bytes endpoint — served from our GCS copy, Drive fallback. */
export function bolDownloadUrl(bolId: string, disposition: 'inline' | 'attachment' = 'attachment'): string {
  return `/order-bols/${bolId}/download?disposition=${disposition}`;
}

export interface BolReviewListResponse {
  items: BolReviewItem[];
  total: number;
}

export type BolDismissReason =
  | 'not_a_bol'
  | 'blank_page'
  | 'duplicate'
  | 'illegible'
  | 'other';

export interface BolDismissResponse {
  id: string;
  extraction_status: string;
  dismissed_reason: string | null;
  dismissed_by: string | null;
}

// ── Shipping flow (bolScanProcess) ──────────────────────────────────────

/** FSM states of one bolScanProcess attempt (db/models/bol_scan_triggers.py). */
export type BolScanTriggerStatus =
  | 'pending'
  | 'processed'
  | 'already_processed'
  | 'needs_input'
  | 'failed'
  | 'resolved'
  | 'dismissed';

/** Fields the sales site can ask for on a needs_input answer. */
export type BolScanNeed =
  | 'tracking_no'
  | 'freight_costs'
  | 'fuel_surcharges'
  | 'drop_off_charge';

export interface BolScanTriggerItem {
  id: string;
  order_bol_id: string;
  order_id: string | null;
  sage_order_no: string;
  status: BolScanTriggerStatus | string;
  reason: 'barcode_link' | 'manual_link' | 'resubmission' | string;
  triggered_by: string;
  target_env: 'prod' | 'dev' | string;
  request_url: string;
  request_payload: Record<string, unknown>;
  response_payload: Record<string, unknown> | null;
  http_status: number | null;
  error: string | null;
  needs: string[] | null;
  /** Sales-site explanation on needs_input (e.g. 'freight_quote_failed'). */
  needs_reason: string | null;
  /** The carrier's own message behind that reason. */
  needs_detail: string | null;
  /** CSR alert raised for a freight ask; null for the classic gates. */
  red_flag_id: string | null;
  ship_via: string | null;
  customer_name: string | null;
  tracking_no: string | null;
  barcode_payloads: string[] | null;
  resubmitted_from_id: string | null;
  resolved_by_id: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  completed_at: string | null;
  created_at: string;
  /** Joined from the scanned page. */
  filename: string | null;
  display_name: string | null;
  page_number: number | null;
}

export interface ShippingFlowState {
  enabled: boolean;
  /** 'off' | 'shadow' (records what would be sent, never calls) | 'live' */
  mode: 'off' | 'shadow' | 'live' | string;
  target_env: 'prod' | 'dev' | string;
  target_url: string;
  refusal: string | null;
}

export interface BolScanTriggerQueueResponse {
  shipping_flow: ShippingFlowState;
  needs_input: BolScanTriggerItem[];
  shipped: BolScanTriggerItem[];
}

export interface BolScanCompleteInput {
  tracking_no?: string | null;
  skip_tracking?: boolean;
  freight_costs?: number | null;
  fuel_surcharges?: number | null;
  drop_off_charge?: number | null;
}

export interface BolManualLinkResponse {
  id: string;
  order_id: string | null;
  sage_order_no: string | null;
  link_source: string | null;
  extraction_status: string;
  /** The shipping-flow attempt the link caused; null when the flag is off. */
  scan_trigger?: BolScanTriggerItem | null;
}

export const bolReviewService = {
  /** Open the scan in a new tab through the authenticated proxy (no Drive access needed). */
  async openPdf(bolId: string): Promise<void> {
    await openAuthedFile(bolDownloadUrl(bolId, 'inline'));
  },

  /** Save the scan under its display name through the authenticated proxy. */
  async downloadPdf(bolId: string, filename: string): Promise<void> {
    await downloadAuthedFile(bolDownloadUrl(bolId, 'attachment'), filename);
  },

  /** BOLs awaiting a human link decision, oldest first. */
  async listNeedsReview(): Promise<BolReviewListResponse> {
    const res = await api.get<BolReviewListResponse>('/order-bols/needs-review');
    return res.data;
  },

  /**
   * Link a BOL to an order by hand. The order number is verified server-side
   * against real orders, so an unknown number rejects with 400 rather than
   * creating a phantom link. When the shipping flow is on, the response also
   * carries the bolScanProcess attempt the link caused.
   */
  async link(bolId: string, sageOrderNo: string): Promise<BolManualLinkResponse> {
    const res = await api.post<BolManualLinkResponse>(
      `/order-bols/${bolId}/link`,
      { sage_order_no: sageOrderNo },
    );
    return res.data;
  },

  /**
   * Take a page out of the queue as not a linkable BOL. Nothing is deleted —
   * the row and its extraction history persist, and the dismissal is audited.
   */
  async dismiss(
    bolId: string,
    reason: BolDismissReason,
  ): Promise<BolDismissResponse> {
    const res = await api.post<BolDismissResponse>(
      `/order-bols/${bolId}/dismiss`,
      { reason },
    );
    return res.data;
  },

  /** Flag state + NEEDS INPUT rows + recent SHIPPED VIA PORTAL confirmations. */
  async listScanTriggers(): Promise<BolScanTriggerQueueResponse> {
    const res = await api.get<BolScanTriggerQueueResponse>('/order-bols/scan-triggers');
    return res.data;
  },

  /**
   * Resubmit a needs_input / failed attempt with the values the sales site
   * asked for. Returns the NEW attempt row; its status says what happened.
   */
  async completeScanTrigger(
    triggerId: string,
    values: BolScanCompleteInput,
  ): Promise<BolScanTriggerItem> {
    const res = await api.post<BolScanTriggerItem>(
      `/order-bols/scan-triggers/${triggerId}/complete`,
      values,
    );
    return res.data;
  },

  /** The warehouse gun handled this one — stop asking. Audited, nothing deleted. */
  async dismissScanTrigger(
    triggerId: string,
    note: string | null,
  ): Promise<BolScanTriggerItem> {
    const res = await api.post<BolScanTriggerItem>(
      `/order-bols/scan-triggers/${triggerId}/dismiss`,
      { note },
    );
    return res.data;
  },
};
